/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Box, Grid, Stack } from "@mui/material";
import FormField from "components/generals/FormInput";
import FormikForm from "components/layout/FormikForm";
import { Form, FormikState } from "formik";
import { checkFormValidity, DoctorFormYUPValidation } from "utils/form.utils";
import { ProviderInterface, HeaderEnum } from "medicalsystem-shared";
import useHttp from "hooks/http.hook";
import { FormProps } from "interfaces";
import StepperFormActionButtons from "../custom/StepperFormActionButtons";
import Typography from "components/generals/Typography";
import BadgeIcon from "@mui/icons-material/Badge";
import { useTranslation } from "react-i18next";
import FormInputPassword from "components/generals/FormInputPassword";
import { FormActionType } from "types/form.enum";
import { Method } from "axios";
import { CoreAPI_URLs } from "constants/http.const";
import { useState } from "react";

/**
 * Props for the DoctorForm component.
 * @interface DoctorFormProps
 * @extends {FormProps<ProviderInterface>}
 * @property {boolean} [isDoctor] - Flag indicating if the provider is a doctor (vs nurse)
 * @property {(updatedModel: Partial<ProviderInterface>) => void} [updateListHandler] - Callback to handle provider updates
 */
interface DoctorFormProps extends FormProps<ProviderInterface> {
  isDoctor?: boolean;
  updateListHandler?: (updatedModel: Partial<ProviderInterface>) => void;
}

/**
 * Form component for doctor/provider professional credentials and license information.
 * Handles medical license, specialty, DEA registration, and NPI number.
 * Can be used standalone or as part of a stepper workflow.
 * 
 * Features:
 * - Medical license number input
 * - Specialty field
 * - DEA registration (optional, removed if empty)
 * - NPI (National Provider Identifier) number
 * - Password field for new providers
 * - Supports both doctor and nurse modes
 * - Form validation with Yup schema
 * - Integration with stepper for multi-step forms
 * - API integration for create/update operations
 * 
 * Use Cases:
 * - Creating new doctor/provider accounts
 * - Updating existing provider credentials
 * - Part of multi-step provider registration
 * 
 * @param {DoctorFormProps} props - Component props
 * @returns {React.ReactElement} The doctor credentials form
 */
const DoctorForm: React.FC<DoctorFormProps> = ({
  model,
  isDoctor,
  action,
  closeAction,
  nextStep,
  backStepHandler,
  lastStep,
  firstStep,
  ...props
}) => {
  const [isLoading, _error, sendRequest] = useHttp();

  const { t } = useTranslation();
  const titleData = isDoctor ? t("doctorForm.doctorData") : t("doctorForm.nurseData");

  const submitHandler = (model: Partial<ProviderInterface>) => {
    if (model.deaRegistration == "") {
      model.deaRegistration = undefined;
    }
    if (nextStep) {
      nextStep(model);
    } else {
      const method: Method = action === FormActionType.NEW ? "POST" : "PUT";
      const successMessageText = action === FormActionType.NEW ? t("doctorForm.providerCreated") : t("doctorForm.providerUpdated");
      sendRequest(
        CoreAPI_URLs.PROVIDER_GENERAL_OPERATIONS,
        onDoctorUpdateHandler,
        method,
        JSON.stringify(model),
        [{ header: HeaderEnum.RESOURCE_ID, value: model.id }],
        { successMessage: successMessageText}
      );
    }
  };

  const onDoctorUpdateHandler = (doctor: ProviderInterface) => {
    if (action === FormActionType.MODIFY) {
      props.setModelList(props.modelList.map((model) => (model.id === doctor.id ? doctor : model)));
    } else {
      props.setModelList((oldArray) => [...oldArray, doctor]);
    }
  };

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandler(model);
      }}
      initialValues={model}
      validationSchema={DoctorFormYUPValidation(t)}
    >
      {({ values, errors, touched }: FormikState<Partial<ProviderInterface>>) => (
        <Form id="doctorForm" autoComplete="true">
          <Stack alignItems="center" direction="row" gap={2} mb={3}>
            <BadgeIcon fontSize="large" />
            <Typography variant="h4">{titleData}</Typography>
          </Stack>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormInputPassword
                label={t("doctorForm.medicalLicense")}
                name="medicalLicense"
                value={values.medicalLicense || ""}
                error={errors.medicalLicense && touched.medicalLicense}
                showModal={true}
                modalBody={t("doctorForm.wouldShowML")}
                modalTitle={t("doctorForm.showML")}
                forceHidePassword={!!values.id}
              />
            </Grid>
            
            <Grid item xs={12} md={isDoctor ? 6 : 12} lg={isDoctor ? 6 : 12}>
              <FormField
                type="Text"
                label={t("doctorForm.speciality")}
                name="specialty"
                value={values.specialty || ""}
                error={errors.specialty && touched.specialty}
              />
            </Grid>
            
            {/* Campo DEA - solo mostrar si isDoctor es true */}
            {isDoctor && (
              <Grid item xs={12} md={6} lg={6}>
                <FormInputPassword
                  label={t("doctorForm.deaRegistration")}
                  name="deaRegistration"
                  value={values.deaRegistration || ""}
                  error={errors.deaRegistration && touched.deaRegistration}
                  showModal={true}
                  modalBody={t("doctorForm.wouldShowDea")}
                  modalTitle={t("doctorForm.showDea")}
                  forceHidePassword={!!values.id}
                />
              </Grid>
            )}
          </Grid>
          
          {checkFormValidity(values, errors, touched) && (
            <Box mt={2} mb={2}>
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'red',
                  textAlign: 'center'
                }}
              >
                {checkFormValidity(values, errors, touched)}
              </Typography>
            </Box>
          )}
          
          <StepperFormActionButtons
            backStepHandler={backStepHandler}
            nextStep={nextStep}
            firstStep={firstStep}
            lastStep={lastStep}
            isLoading={isLoading}
          />
        </Form>
      )}
    </FormikForm>
  );
};

export default DoctorForm;